import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  AlertTitle,
  CircularProgress,
  TextField,
  useTheme,
  alpha,
  Divider,
  Chip
} from '@mui/material';
import {
  CheckCircle as SuccessIcon,
  ArrowForward as NextIcon,
  History as HistoryIcon,
  Psychology as IntentIcon
} from '@mui/icons-material';
import { useWorkflowContext } from '../../context/WorkflowContext';
import { snowflakeApi } from '../../utils/api';
import { useToast } from '../../context/ToastContext';
import { useAuthTokenCheck } from '../../hooks/useAuthTokenCheck';
import config from '../../utils/config';
import AIResponseStreaming from '../../components/workflow-v2/AIResponseStreaming';
import SessionExpiredDialog from '../../components/workflow-v2/SessionExpiredDialog';
import ModelSelectorButton from '../../components/workflow-v2/ModelSelectorButton';
import TestRunButton from '../../components/workflow-v2/TestRunButton';
import KeywordPreviewPanel from '../../components/workflow-v2/KeywordPreviewPanel';
import PromptEditorV2 from '../../components/workflow-v2/PromptEditorV2';

const SemanticAnalysisStep: React.FC = () => {
  const { clientId, workflowId } = useParams<{ clientId: string; workflowId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const { showToast } = useToast();
  const { workflow } = useWorkflowContext();
  
  const [customCategories, setCustomCategories] = useState<string>('');
  const [customPrompt, setCustomPrompt] = useState<string>('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [streamedContent, setStreamedContent] = useState<string>('');
  const [llmModel, setLlmModel] = useState<string>('');
  const [testKeywordLimit, setTestKeywordLimit] = useState<number | null>(null);
  const [mostRecentModuleIsUpstream, setMostRecentModuleIsUpstream] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isMerging, setIsMerging] = useState(false);
  
  // Use auth token check hook
  const { validateToken, showSessionExpiredDialog, setShowSessionExpiredDialog } = useAuthTokenCheck();
  
  // Memoized callback for prompt changes
  const handlePromptChange = useCallback((prompt: string) => {
    setCustomPrompt(prompt || "");
  }, []);
  
  // Check most recent module to determine if we should show old results
  useEffect(() => {
    const checkModules = async () => {
      if (!workflowId) {
        return;
      }
      
      try {
        setIsInitialLoading(true);
        
        // Import workflowService
        const { workflowService } = await import('../../services/api/workflowService');
        
        // Get workflow (includes modules in response)
        const dbWorkflow = await workflowService.getDbWorkflow(workflowId);
        const allModules = dbWorkflow.modules || [];
        
        // Find the most recent module (highest module_id)
        let mostRecentModule = null;
        let highestModuleId = -1;
        
        allModules.forEach((module: any) => {
          if (module.module_id && module.module_id > highestModuleId) {
            highestModuleId = module.module_id;
            mostRecentModule = module;
          }
        });
        
        // Check if most recent module is upstream (fileupload_v2 or module1_v2)
        const moduleType = mostRecentModule?.type || mostRecentModule?.module_type;
        const upstreamTypes = ['fileupload_v2', 'module1_v2'];
        const isUpstream = moduleType && upstreamTypes.includes(moduleType);
        
        setMostRecentModuleIsUpstream(!!isUpstream);
        console.log('[SemanticAnalysis] Most recent module:', moduleType, '| Is upstream:', isUpstream);
        
        // If most recent is upstream, skip loading previous results
        if (isUpstream) {
          console.log('[SemanticAnalysis] Skipping previous result load - upstream module is most recent');
          setIsInitialLoading(false);
          return;
        }
        
        // Otherwise, load previous results
        const statusUrl = `${config.api.snowflake}/api/v1/workflow-v2/semantic-analysis/${workflowId}/status`;
        const response = await fetch(statusUrl);
        
        if (response.ok) {
          const data = await response.json();
          
          // Treat both 'completed' and 'pending_for_merge' as complete
          if ((data.status === 'completed' || data.status === 'pending_for_merge') && data.result) {
            setStreamedContent(data.result);
            setAnalysisComplete(true);
            console.log('[SemanticAnalysis] Loaded previous analysis result with status:', data.status);
          } else if (data.status === 'processing') {
            showToast('Previous analysis is still in progress', 'warning');
          } else if (data.status === 'failed') {
            showToast(`Previous analysis failed: ${data.error_message || 'Unknown error'}`, 'error');
          }
        }
      } catch (error) {
        console.error('Error checking modules:', error);
        setMostRecentModuleIsUpstream(false);
      } finally {
        setIsInitialLoading(false);
      }
    };
    
    checkModules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowId]);
  
  
  // Handle model selection
  const handleModelSelect = (modelValue: string) => {
    setLlmModel(modelValue);
  };
  
  // Handle test run
  const handleTestRun = (keywordLimit: number) => {
    setTestKeywordLimit(keywordLimit);
    // Immediately start analysis with this limit
    handleStartAnalysis(keywordLimit);
  };
  
  // Handle semantic analysis submission
  const handleStartAnalysis = async (keywordLimitOverride?: number) => {
    if (!clientId || !workflowId) {
      showToast('Missing client ID or workflow ID', 'error');
      return;
    }

    setIsAnalyzing(true);
    setStreamedContent('');
    setAnalysisComplete(false);
    
    try {
      // Validate authentication token
      const { isValid, token } = validateToken();
      if (!isValid) {
        setIsAnalyzing(false);
        return;
      }

      // Parse custom categories if provided
      const categoryList = customCategories
        ? customCategories.split(',').map(cat => cat.trim()).filter(cat => cat.length > 0)
        : [];

      // Call backend streaming endpoint using absolute URL
      const apiUrl = `${config.api.snowflake}/api/v1/workflow-v2/semantic-analysis/stream`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          client_id: clientId,
          workflow_id: workflowId,
          custom_categories: categoryList.length > 0 ? categoryList : null,
          custom_prompt: customPrompt,
          keyword_limit: keywordLimitOverride || null,
          model: llmModel
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (!response.body) {
        throw new Error('No response body');
      }

      // Read the streaming response
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let accumulatedContent = '';

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          // Stream completed successfully
          setAnalysisComplete(true);
          // Analysis created a new module3_v2 record, so upstream modules are no longer the most recent
          setMostRecentModuleIsUpstream(false);
          showToast('Semantic analysis completed successfully!', 'success');
          break;
        }

        // Decode chunk and append
        const chunk = decoder.decode(value, { stream: true });
        accumulatedContent += chunk;
        setStreamedContent(accumulatedContent);
      }
      
    } catch (error) {
      console.error('Error during analysis:', error);
      showToast('Failed to complete semantic analysis', 'error');
    } finally {
      setIsAnalyzing(false);
    }
  };
  
  const handleRerun = () => {
    setAnalysisComplete(false);
    setTestKeywordLimit(null);
    // Don't clear streamedContent so the "View Latest Results" alert remains visible
  };
  
  const handleConfirmAndMerge = async () => {
    if (!clientId || !workflowId) {
      showToast('Missing client ID or workflow ID', 'error');
      return;
    }
    
    setIsMerging(true);
    
    try {
      showToast('Merging Module 1 and Module 3 results...', 'info');
      
      // Call merge endpoint
      const response = await snowflakeApi.post('/api/v1/workflow-v2/merge-frameworks', {
        client_id: clientId,
        workflow_id: workflowId
      });
      
      if (response.data.success) {
        showToast('Frameworks merged successfully!', 'success');
        // Navigate to batch config page
        navigate(`/workflow2/${clientId}/${workflowId}/batch_config`);
      } else {
        throw new Error('Merge failed');
      }
    } catch (error) {
      console.error('Error merging frameworks:', error);
      showToast('Failed to merge frameworks', 'error');
      setIsMerging(false);
    }
  };
  
  // Show loading spinner while checking modules and loading previous results
  if (isInitialLoading) {
    return (
      <Box>
        <Typography variant="body1" color="text.secondary" paragraph>
          Analyze queries to discover user intent patterns and behavioral categories.
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={40} sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Loading workflow data...
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }
  
  return (
    <Box>
      <Typography variant="body1" color="text.secondary" paragraph>
        Analyze queries to discover user intent patterns and behavioral categories.
      </Typography>
      
      {/* Main content */}
      <Box>
          {/* Configuration Section - Show when not analyzing and (not complete OR most recent is upstream) */}
          {!isAnalyzing && (!analysisComplete || mostRecentModuleIsUpstream) && (
            <Box sx={{ mb: 3 }}>
              {/* Show button to view latest results if they exist (but not if upstream module is most recent) */}
              {streamedContent && !mostRecentModuleIsUpstream && (
                <Alert 
                  severity="info" 
                  sx={{ mb: 2 }}
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      variant="outlined"
                      onClick={() => setAnalysisComplete(true)}
                      startIcon={<HistoryIcon />}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      View Latest Results
                    </Button>
                  }
                >
                  <AlertTitle>Previous Analysis Available</AlertTitle>
                  The last semantic analysis completed successfully. You can view the results or start a new analysis below.
                </Alert>
              )}
              
              <Box sx={{ height: '900px', display: 'flex', gap: 3 }}>
                {/* Left: Configuration Form (2/3 width) */}
                <Box sx={{ flex: '2', display: 'flex' }}>
                  <Paper
                    elevation={2}
                    sx={{
                      p: 3,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      backgroundColor: alpha(theme.palette.background.paper, 0.9),
                      width: '100%',
                      overflow: 'auto'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <IntentIcon sx={{ color: theme.palette.primary.main, fontSize: 28 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Intent Analysis Configuration
                      </Typography>
                    </Box>
                    
                    <Divider sx={{ mb: 1 }} />
                    
                    <Box>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        This analysis will discover user intent patterns in your queries, such as:
                      </Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                        <Chip label="Find Near Me" size="small" variant="outlined" />
                        <Chip label="Compare Prices" size="small" variant="outlined" />
                        <Chip label="Learn How To" size="small" variant="outlined" />
                        <Chip label="Troubleshoot Problems" size="small" variant="outlined" />
                        <Chip label="Buy Now" size="small" variant="outlined" />
                      </Box>
                    </Box>
                    
                    {/* Custom Categories Input */}
                    <TextField
                      fullWidth
                      label="Custom Intent Categories (Optional)"
                      multiline
                      rows={4}
                      value={customCategories}
                      onChange={(e) => setCustomCategories(e.target.value)}
                      placeholder="e.g., Find Near Me, Compare Prices, Learn How To, Troubleshoot"
                      helperText="Comma-separated list of intent categories to guide the AI analysis. Leave empty to let AI discover patterns automatically."
                      sx={{ mb: 3 }}
                    />
                    
                    {/* Divider before prompt editor */}
                    <Divider sx={{ my: 2 }} />
                    
                    {/* Prompt Editor */}
                    <Box>
                      <Typography variant="subtitle1" fontWeight={500} mb={1}>
                        Intent Analysis
                      </Typography>
                      <PromptEditorV2
                        module="module3_v2"
                        title="Intent Analysis"
                        description="Customize the intent analysis based on existing intent categories or other preferences. PLEASE REVIEW CAREFULLY"
                        onPromptChange={handlePromptChange}
                      />
                    </Box>
                  
                    <Box sx={{ flex: 1 }} />

                    {/* Model Selection and Submit Button */}
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      {/* Model Selector Button */}
                      <ModelSelectorButton
                        selectedModel={llmModel}
                        onModelSelect={handleModelSelect}
                        disabled={isAnalyzing}
                      />
                      
                      {/* Test Run Button */}
                      <TestRunButton
                        onTestRun={handleTestRun}
                        disabled={isAnalyzing}
                        currentLimit={testKeywordLimit}
                      />
                      
                      {/* Submit Button */}
                      <Button
                        variant="contained"
                        size="large"
                        onClick={() => handleStartAnalysis()}
                        disabled={isAnalyzing}
                        fullWidth
                        sx={{ py: 1.5 }}
                      >
                        {isAnalyzing ? 'Analyzing Intent Patterns...' : 'Start AI Analysis'}
                      </Button>
                    </Box>
                  </Paper>
                </Box>
                
                {/* Right: Queries Preview (fixed 300px width) */}
                <KeywordPreviewPanel 
                  clientId={clientId!} 
                  workflowId={workflowId!} 
                  limit={1000}
                />
              </Box>
            </Box>
          )}
          
          {/* Results Panel - Show when analyzing or complete
              Hide old completed results if most recent module is upstream, but always show if currently analyzing */}
          {(isAnalyzing || (analysisComplete && !mostRecentModuleIsUpstream)) && (
            <Paper
              elevation={3}
              sx={{
                p: 4,
                backgroundColor: analysisComplete 
                  ? alpha(theme.palette.success.light, 0.05)
                  : alpha(theme.palette.background.paper, 0.9),
                border: analysisComplete 
                  ? `2px solid ${alpha(theme.palette.success.main, 0.3)}`
                  : 'none',
              }}
            >
              {/* Success header - only show when complete */}
              {analysisComplete && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <SuccessIcon sx={{ fontSize: 48, color: 'success.main' }} />
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 600, color: 'success.main' }}>
                        Intent Analysis Complete!
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {testKeywordLimit 
                          ? `Test analysis completed with ${testKeywordLimit} queries (not saved to database)`
                          : 'Intent categories have been discovered and saved to database'
                        }
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    variant="outlined"
                    onClick={handleRerun}
                    sx={{ flexShrink: 0 }}
                  >
                    Re-run Analysis
                  </Button>
                </Box>
              )}
              
              {/* AI Response - handles both streaming and completed states */}
              <AIResponseStreaming
                isStreaming={isAnalyzing}
                streamedContent={streamedContent}
                isComplete={analysisComplete}
                showRawByDefault={false}
              />
              
              {/* Action buttons - only show when complete */}
              {analysisComplete && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
                  <Button
                    variant="outlined"
                    onClick={handleRerun}
                    size="large"
                  >
                    Re-run Analysis
                  </Button>
                  {!testKeywordLimit && (
                    <Button
                      variant="contained"
                      color="success"
                      endIcon={isMerging ? <CircularProgress size={20} color="inherit" /> : <NextIcon />}
                      onClick={handleConfirmAndMerge}
                      disabled={isMerging}
                      size="large"
                    >
                      {isMerging ? 'Merging...' : 'Confirm & Proceed to Batch Config'}
                    </Button>
                  )}
                  {testKeywordLimit && (
                    <Alert severity="info" sx={{ flex: 1, ml: 2 }}>
                      This was a test run with {testKeywordLimit} queries. Click "Start AI Analysis" to run full analysis and proceed to Batch Config.
                    </Alert>
                  )}
                </Box>
              )}
            </Paper>
          )}
        </Box>


      {/* Session Expired Dialog */}
      <SessionExpiredDialog
        open={showSessionExpiredDialog}
        onClose={() => setShowSessionExpiredDialog(false)}
      />
    </Box>
  );
};

export default SemanticAnalysisStep;
